/**
 * AddressModal
 * Reusable modal for collecting/editing customer address with autocomplete.
 * Integrates with AddressAutocompleteInput for street-level suggestions.
 *
 * Usage:
 *   <AddressModal
 *     isOpen={showAddressModal}
 *     onClose={() => setShowAddressModal(false)}
 *     onAddressSave={handleAddressSave}
 *     initialAddress={{ street, city, state, zipCode }}
 *     isRequired={true}
 *     title="Add Your Address"
 *   />
 */
import React, { useState, useEffect } from "react";
import AddressAutocompleteInput from "./AddressAutocompleteInput";
import "./AddressModal.css";

const AddressModal = ({
  isOpen,
  onClose,
  onAddressSave,
  initialAddress = {},
  isRequired = false,
  title = "Add Your Address",
  subtitle = "We need your address for tax calculation and order delivery",
}) => {
  const [address, setAddress] = useState({
    street: initialAddress.street || "",
    city: initialAddress.city || "",
    state: initialAddress.state || "",
    zipCode: initialAddress.zipCode || "",
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAddress(initialAddress);
      setError("");
    }
  }, [isOpen, initialAddress]);

  const handleAddressSelect = (parsedAddress) => {
    // When autocomplete selects a place, fill in all fields
    setAddress((prev) => ({
      ...prev,
      street: parsedAddress.street || prev.street,
      city: parsedAddress.city || prev.city,
      state: parsedAddress.state || prev.state,
      zipCode: parsedAddress.zip || prev.zipCode,
    }));
  };

  const validateAddress = () => {
    if (!address.street.trim()) {
      setError("Street address is required");
      return false;
    }
    if (!address.city.trim()) {
      setError("City is required");
      return false;
    }
    if (!address.state.trim()) {
      setError("State is required");
      return false;
    }
    if (!address.zipCode.trim()) {
      setError("ZIP code is required");
      return false;
    }
    if (!/^\d{5}(-\d{4})?$/.test(address.zipCode)) {
      setError("Please enter a valid ZIP code (e.g., 12345 or 12345-6789)");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateAddress()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      await onAddressSave(address);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save address");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="address-modal-overlay">
      <div className="address-modal">
        <div className="address-modal-header">
          <h2>{title}</h2>
          {!isRequired && (
            <button className="address-modal-close" onClick={onClose}>
              ✕
            </button>
          )}
        </div>

        {subtitle && <p className="address-modal-subtitle">{subtitle}</p>}

        <div className="address-modal-body">
          {error && <div className="address-modal-error">{error}</div>}

          <div className="address-form-group">
            <label htmlFor="street-input">Street Address *</label>
            <AddressAutocompleteInput
              value={address.street}
              onChange={(val) =>
                setAddress((prev) => ({ ...prev, street: val }))
              }
              onAddressSelect={handleAddressSelect}
              placeholder="Enter street address"
              required
              inputStyle={{
                width: "100%",
                padding: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>

          <div className="address-form-row">
            <div className="address-form-group">
              <label htmlFor="city-input">City *</label>
              <input
                id="city-input"
                type="text"
                value={address.city}
                onChange={(e) =>
                  setAddress((prev) => ({ ...prev, city: e.target.value }))
                }
                placeholder="City"
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>

            <div className="address-form-group">
              <label htmlFor="state-input">State *</label>
              <input
                id="state-input"
                type="text"
                value={address.state}
                onChange={(e) =>
                  setAddress((prev) => ({ ...prev, state: e.target.value }))
                }
                placeholder="State (e.g., IL)"
                maxLength="2"
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>

          <div className="address-form-group">
            <label htmlFor="zip-input">ZIP Code *</label>
            <input
              id="zip-input"
              type="text"
              value={address.zipCode}
              onChange={(e) =>
                setAddress((prev) => ({ ...prev, zipCode: e.target.value }))
              }
              placeholder="ZIP code"
              required
              style={{
                width: "100%",
                padding: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        <div className="address-modal-footer">
          <button
            className="address-modal-btn secondary"
            onClick={onClose}
            disabled={loading || isRequired}
          >
            {isRequired ? "Address Required" : "Cancel"}
          </button>
          <button
            className="address-modal-btn primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Address"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddressModal;
